import { assertNever } from "@msys/common";
import { LabeledValue, Modal } from "@msys/ui";
import { Unit } from "@msys/units";
import { Alert, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useField } from "formik";
import { capitalize, omit, uniqueId } from "lodash-es";
import React from "react";
import { v4 } from "uuid";
import * as Yup from "yup";
import {
  DefineItemProps2Entry,
  DefineItemProps2EntryBool,
  DefineItemProps2EntryNumber,
  DefineItemProps2EntryNumberArray,
  DefineItemProps2EntryText,
  DefineItemProps2EntryTextArray,
  DocType,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { AutocompleteField } from "../../../commons/form-fields/AutocompleteField.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { ItemField } from "../form-fields/ItemField.js";
import {
  AllowedValuesField,
  AllowedValuesFormValues,
  useAllowedValuesFieldValidationSchema,
} from "../form-fields/PropertyAllowedValuesField.js";
import {
  getValuesMediaBool,
  useValuesMediaBoolFieldValidationSchema,
  ValuesMediaBoolField,
  ValuesMediaBoolFormValues,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import { ItemSelect_ItemFragment } from "../ItemSelect.generated.js";
import {
  NumberRangeSection,
  NumberRangeSectionFormValues,
  useNumberRangeValidationSchema,
} from "../NumberRangeSection.js";
import { PROP_VALUES, TypeFields } from "../properties.js";
import {
  defaultQuestionControlValues,
  QuestionControlSection,
  QuestionControlSectionFormValues,
  useQuestionControlValidationSchema,
} from "../QuestionControlSection.js";
import { usePropertyUnits } from "../usePropertyUnits.js";

interface FormValues
  extends TypeFields,
    AllowedValuesFormValues,
    ValuesMediaBoolFormValues,
    QuestionControlSectionFormValues,
    NumberRangeSectionFormValues {
  key: string;
  label: string;
  unit: Unit | null;
  essential: boolean;
  customizable: boolean;
  group: string;
  clientVisibility: boolean;
  askIfExpr: string;
  itemId: string | null;
}

interface Props {
  handleClose: () => void;
  handleComplete: (
    newProperty: DefineItemProps2Entry,
    values: FormValues
  ) => Promise<void>;
  docType: DocType | null;
  showQuestionControl?: boolean;
  showAskIfExpr?: boolean;
  showItemSelect?: boolean;
  initialItemId?: string | null;
  // we need it for ask if expressions
  projectId?: string | null;
  docId?: string;
  itemId?: string;
  // for item select
  items?: ItemSelect_ItemFragment[];
}

type NoUndefinedProps<T> = {
  [P in keyof T]-?: Exclude<T[P], undefined>;
};

export function ItemPropertyAddModal(
  props: Omit<
    Props,
    | "projectId"
    | "docId"
    | "itemId"
    | "showQuestionControl"
    | "showAskIfExpr"
    | "showItemSelect"
    | "items"
    | "initialItemId"
  > &
    NoUndefinedProps<Pick<Props, "projectId" | "docId" | "itemId">>
) {
  return (
    <PropertyAddModal
      showQuestionControl={true}
      showAskIfExpr={true}
      showItemSelect={false}
      {...props}
    />
  );
}

export function ItemPropertyAddModalWithItemSelect(
  props: Omit<
    Props,
    | "projectId"
    | "docId"
    | "itemId"
    | "showQuestionControl"
    | "showAskIfExpr"
    | "showItemSelect"
    | "items"
  > &
    NoUndefinedProps<Pick<Props, "projectId" | "docId" | "items">>
) {
  return (
    <PropertyAddModal
      showQuestionControl={true}
      showAskIfExpr={true}
      showItemSelect={true}
      {...props}
    />
  );
}

export function ProductPropertyAddModal(
  props: Omit<
    Props,
    | "docType"
    | "projectId"
    | "docId"
    | "itemId"
    | "showQuestionControl"
    | "showAskIfExpr"
    | "showItemSelect"
    | "items"
    | "initialItemId"
  >
) {
  return (
    <PropertyAddModal
      showQuestionControl={false}
      showAskIfExpr={false}
      showItemSelect={false}
      docType={null}
      {...props}
      // TODO: do we need to clear those?
      // handleComplete={newProperty =>
      //   props.handleComplete(
      //     omit(newProperty, "askIfExpr", "prompt", "askWhen", "askWhom")
      //   )
      // }
    />
  );
}

function PropertyAddModal({
  handleClose,
  handleComplete,
  docType,
  showQuestionControl = true,

  // item select
  showItemSelect = false,
  initialItemId,
  items,

  // ask if expression
  showAskIfExpr = true,
  projectId,
  docId,
  itemId,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { getUnits } = usePropertyUnits();
  const unitsOptions = React.useMemo(() => getUnits(), [getUnits]);
  const [error, setError] = React.useState<Error | null>(null);

  const initialValues = React.useMemo(
    (): FormValues => ({
      itemId: initialItemId || null,
      type: "TEXT",
      isArray: false,
      key: "",
      label: "",
      defineAllowedValues: false,
      allowedValues: [
        {
          key: v4(),
          value: null,
          media: null,
        },
      ],
      defineValuesMediaBool: false,
      valuesMediaBool: getValuesMediaBool(null),
      setRange: false,
      setStep: false,
      range: {
        min: null,
        max: null,
      },
      essential: false,
      customizable: false,
      unit: null,
      step: null,
      group: "",
      clientVisibility: false,
      ...defaultQuestionControlValues,
      askIfExpr: "",
    }),
    [initialItemId]
  );

  const allowedValuesSchema = useAllowedValuesFieldValidationSchema(null);
  const valuesMediaBoolSchema = useValuesMediaBoolFieldValidationSchema();
  const questionControlSchema = useQuestionControlValidationSchema();
  const numberRangeSchema = useNumberRangeValidationSchema(null);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        itemId: Yup.string()
          .label(t("Item", { ns: "Global" }))
          .when([], {
            is: () => showItemSelect,
            then: schema => schema.required(),
          })
          .nullable(),
        type: Yup.string().oneOf(PROP_VALUES).required(),
        isArray: Yup.boolean().required(),
        key: Yup.string()
          .required()
          .min(1)
          .label(t("Key", { ns: "Global" })),
        label: Yup.string()
          .required()
          .min(1)
          .label(t("Label", { ns: "Global" })),
        essential: Yup.boolean().required(),
        customizable: Yup.boolean().required(),
        unit: Yup.object().shape({ key: Yup.string().required() }).nullable(),
        group: Yup.string(),
        ...numberRangeSchema,
        ...allowedValuesSchema,
        ...valuesMediaBoolSchema,
        ...questionControlSchema,
        askIfExpr: Yup.string(),
      }),
    [
      t,
      numberRangeSchema,
      allowedValuesSchema,
      valuesMediaBoolSchema,
      questionControlSchema,
      showItemSelect,
    ]
  );

  const onSubmit = async (values: FormValues) => {
    const newProperty: DefineItemProps2Entry = createPropertyEntry(values);
    try {
      await handleComplete(newProperty, values);
      handleClose();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(JSON.stringify(err));
      setError(error);
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {formikProps => (
        <Modal
          title={t("Add property", {
            ns: "QuoteItem",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Confirm", {
                ns: "Global",
              }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              {error && <Alert severity="error">{error.message}</Alert>}
              {showItemSelect && (
                <ItemField
                  name="itemId"
                  items={items ?? []}
                  inputLabel={t("Item", { ns: "Global" })}
                />
              )}
              <TextField
                name={"label"}
                label={t("Label", {
                  ns: "Global",
                })}
                autoFocus
                helperText={
                  <GeneratedKeyField sourceFieldName="label" name="key" />
                }
              />

              <SelectField
                name={"type"}
                label={t("Type", { ns: "QuoteItem" })}
                options={PROP_VALUES.map(type => ({
                  label: capitalize(type), // TODO: translate ?
                  value: type,
                }))}
                onChange={value => {
                  if (!value) return;
                  formikProps.setValues({
                    ...formikProps.values,
                    type: value,
                    allowedValues: [
                      {
                        key: v4(),
                        value: null,
                        media: null,
                      },
                    ],
                    defineAllowedValues: false,
                  });
                }}
              />
              {formikProps.values.type === "NUMBER" && (
                <AutocompleteField
                  name="unit"
                  inputLabel={t("Unit", { ns: "QuoteItem" })}
                  options={unitsOptions}
                  getOptionLabel={option => option.value}
                />
              )}
              {formikProps.values.type !== "BOOLEAN" && (
                <CheckboxField
                  name={"isArray"}
                  label={t("Allow multiple values", {
                    ns: "QuoteItem",
                  })}
                />
              )}
              {formikProps.values.type !== "BOOLEAN" && (
                <AllowedValuesField
                  type={formikProps.values.type}
                  disabled={
                    (formikProps.values.type === "NUMBER" &&
                      (formikProps.values.setRange ||
                        formikProps.values.setStep)) ||
                    formikProps.isSubmitting
                  }
                />
              )}
              {formikProps.values.type === "BOOLEAN" && (
                <ValuesMediaBoolField disabled={formikProps.isSubmitting} />
              )}
              {formikProps.values.type === "NUMBER" && (
                <NumberRangeSection
                  disabled={formikProps.values.defineAllowedValues}
                />
              )}
              <TextField
                name={"group"}
                label={t("Group", { ns: "QuoteItem" })}
              />
              <CheckboxField
                name={`clientVisibility`}
                label={t("Visibility", { ns: "Global" })}
              />
              {docType === "TEMPLATE" && (
                <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                  <CheckboxField
                    name="customizable"
                    label={t("Customisable", { ns: "QuoteItem" })}
                  />
                </RestrictedByCapabilityWithDebug>
              )}
              {docType && showQuestionControl && (
                <QuestionControlSection isInitiallyExpanded />
              )}
              {/*TODO: temporary commented out*/}
              {/*{docType &&
                showAskIfExpr &&
                !isUndefined(projectId) &&
                !isUndefined(itemId) &&
                !isUndefined(docId) && (
                  <AskIfConditionExpressionField
                    projectId={projectId}
                    itemId={itemId}
                    docId={docId}
                    name={"askIfExpr"}
                    label={t("Ask if condition", { ns: "QuoteItem" })}
                    addButtonLabel={t("Add condition", { ns: "QuoteItem" })}
                    disabled={formikProps.isSubmitting}
                  />
                )}*/}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
}

const GeneratedKeyField = ({
  sourceFieldName,
  name,
}: {
  sourceFieldName: string;
  name: string;
}) => {
  const { t } = useTranslate(["Global"]);

  const [{ value: sourceValue }] = useField(sourceFieldName);
  const [{ value }, _, { setValue }] = useField(name);

  React.useEffect(() => {
    const newValue = sourceValue ? generateKeyFromLabel(sourceValue) : "";

    setValue(newValue);
  }, [setValue, sourceValue]);

  return (
    <>
      {t("Key", { ns: "Global" })}: {value}
    </>
  );
};

export function createPropertyEntry({
  type,
  isArray,
  ...values
}: Omit<FormValues, "itemId">): DefineItemProps2Entry {
  if (!type) throw new Error("Prop type is missing!");

  if (type === "BOOLEAN") {
    return { bool: createBooleanPropertyEntry(values) };
  } else if (type === "NUMBER") {
    if (isArray) {
      return { numberArray: createNumberArrayPropertyEntry(values) };
    }

    return { number: createNumberPropertyEntry(values) };
  } else if (type === "TEXT") {
    if (isArray) {
      return { textArray: createTextArrayPropertyEntry(values) };
    }

    return { text: createTextPropertyEntry(values) };
  } else {
    assertNever(type);
  }
}

type EntryInputValues = Omit<FormValues, keyof TypeFields | "itemId">;

function createBooleanPropertyEntry(
  values: EntryInputValues
): DefineItemProps2EntryBool {
  return {
    key: values.key,
    label: values.label || values.key,
    group: values.group,
    essential: values.essential,
    valuesMediaBool: values.defineValuesMediaBool
      ? values.valuesMediaBool
          .filter(v => v.media)
          .map(v => ({
            bool: v.value,
            media: omit(v.media!, "id", "__type"),
          }))
      : [],
    valueBool: undefined,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createNumberPropertyEntry(
  values: EntryInputValues
): DefineItemProps2EntryNumber {
  return {
    key: values.key,
    label: values.label || values.key,
    group: values.group,
    essential: values.essential,
    allowedValuesNumber: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedNumber: v.value as number,
          media: v.media ? omit(v.media, "id", "__type") : null,
        }))
      : [],
    range: values.setRange ? values.range : null,
    step: values.setStep ? values.step : null,
    valueNumber: undefined,
    unit: values.unit?.key,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createNumberArrayPropertyEntry(
  values: EntryInputValues
): DefineItemProps2EntryNumberArray {
  return {
    key: values.key,
    label: values.label || values.key,
    group: values.group,
    essential: values.essential,
    allowedValuesNumber: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedNumber: v.value as number,
          media: v.media ? omit(v.media, "id", "__type") : null,
        }))
      : [],
    range: values.setRange ? values.range : null,
    step: values.setStep ? values.step : null,
    valueNumberArray: undefined,
    unit: values.unit?.key,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createTextPropertyEntry(
  values: EntryInputValues
): DefineItemProps2EntryText {
  return {
    key: values.key,
    label: values.label || values.key,
    group: values.group,
    essential: values.essential,
    allowedValuesText: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedText: v.value as string,
          media: v.media ? omit(v.media, "id", "__type") : null,
        }))
      : [],
    valueText: undefined,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}
function createTextArrayPropertyEntry(
  values: EntryInputValues
): DefineItemProps2EntryTextArray {
  return {
    key: values.key,
    label: values.label || values.key,
    group: values.group,
    essential: values.essential,
    allowedValuesText: values.defineAllowedValues
      ? values.allowedValues.map(v => ({
          allowedText: v.value as string,
          media: v.media ? omit(v.media, "id", "__type") : null,
        }))
      : [],
    valueTextArray: undefined,
    clientVisibility: values.clientVisibility,
    prompt: values.prompt,
    questionControl: values.questionControl,
    // questionControl: [
    //   {
    //     askWhen: values.askWhen,
    //     askWhom: values.askWhom,
    //   },
    // ],
    askIfExpr: values.askIfExpr,
    customizable: values.customizable,
  };
}

function generateKeyFromLabel(label: string): string {
  let id = transliterateBasic(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_") // anything not [a-z0-9] -> _
    .replace(/_+/g, "_") // collapse
    .replace(/^_+|_+$/g, ""); // trim

  if (!id) id = "field";
  if (/^[0-9]/.test(id)) id = "f_" + id;
  if (SQL_TS_RESERVED.has(id)) id = "f_" + id;

  return id;
}

const SQL_TS_RESERVED = new Set([
  // SQL (common)
  "select",
  "from",
  "where",
  "group",
  "order",
  "by",
  "limit",
  "offset",
  "join",
  "left",
  "right",
  "inner",
  "outer",
  "insert",
  "update",
  "delete",
  "into",
  "table",
  "view",
  "index",
  "unique",
  "primary",
  "key",
  "default",
  "null",
  "not",
  "and",
  "or",
  "as",
  "on",
  "case",
  "when",
  "then",
  "else",
  "end",
  "distinct",
  "union",
  "having",
  "exists",
  "true",
  "false",
  // JS/TS
  "break",
  "case",
  "catch",
  "class",
  "const",
  "continue",
  "debugger",
  "default",
  "delete",
  "do",
  "else",
  "enum",
  "export",
  "extends",
  "finally",
  "for",
  "function",
  "if",
  "import",
  "in",
  "instanceof",
  "new",
  "return",
  "super",
  "switch",
  "this",
  "throw",
  "try",
  "typeof",
  "var",
  "void",
  "while",
  "with",
  "yield",
  "implements",
  "interface",
  "let",
  "package",
  "private",
  "protected",
  "public",
  "static",
  "await",
]);

// --- Transliteration (extend as needed or swap for a library like "transliteration") ---
function transliterateBasic(input: string): string {
  return input
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/Ä/g, "Ae")
    .replace(/Ö/g, "Oe")
    .replace(/Ü/g, "Ue")
    .replace(/ß/g, "ss")
    .replace(/[×✕]/g, "x")
    .replace(/ø/g, "o")
    .replace(/Ø/g, "O")
    .replace(/ñ/g, "n")
    .replace(/Ñ/g, "N")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, ""); // strip diacritics
}
